/**
 * MARKET DATA SERVICE - Orchestration Layer
 * Coordinates between API calls and engines
 *
 * @version 3.0.0
 */

import {
  API_BASE_URLS,
  API_ENDPOINTS,
  DEFAULT_REQUEST_OPTIONS,
  CACHE_CONFIG,
  buildApiUrl,
  buildQueryString,
} from '@/lib/config/api-config';
import {
  calculateGrowthScore,
  calculateCompositeScore,
} from '@/lib/engines/growth-score-engine';
import {
  classifyTrendRegime,
  generateTradingSignal,
} from '@/lib/engines/signal-engine';
import type {
  CandleWithIndicators,
  MarketDataResponse,
  Symbol,
  AssetType,
  IntervalString,
  TradingSignal,
} from '@/types';

// ============================================================================
// § 1. SERVICE CLASS
// ============================================================================

class MarketDataService {
  private cache: Map<string, { data: unknown; timestamp: number }> = new Map();

  /**
   * Fetch market data with indicators
   */
  async getMarketData(
    symbol: string,
    interval: IntervalString = '1day',
    limit: number = 100
  ): Promise<MarketDataResponse> {
    const cacheKey = `market_${symbol}_${interval}_${limit}`;
    const cached = this.getFromCache<MarketDataResponse>(cacheKey, CACHE_CONFIG.MARKET_DATA);
    if (cached) return cached;

    const params = buildQueryString({ symbol, interval, limit });
    const url = buildApiUrl(`${API_ENDPOINTS.MARKET_DATA}?${params}`);

    const response = await this.fetchWithRetry(url);
    const data: MarketDataResponse = await response.json();

    // Enhance with calculated fields
    if (data.data && data.data.length > 0) {
      data.data = this.enhanceWithCalculations(data.data);
    }

    this.setCache(cacheKey, data);
    return data;
  }

  /**
   * Fetch historical data
   */
  async getHistoricalData(
    symbol: string,
    startDate: string,
    endDate: string,
    interval: IntervalString = '1day'
  ): Promise<CandleWithIndicators[]> {
    const params = buildQueryString({
      symbol,
      interval,
      start_date: startDate,
      end_date: endDate,
    });
    const url = buildApiUrl(`${API_ENDPOINTS.HISTORICAL_DATA}?${params}`);

    const response = await this.fetchWithRetry(url);
    const data = await response.json();

    return this.enhanceWithCalculations(data.data || []);
  }

  /**
   * Fetch symbols list
   */
  async getSymbols(assetType?: AssetType): Promise<Symbol[]> {
    const cacheKey = `symbols_${assetType || 'all'}`;
    const cached = this.getFromCache<Symbol[]>(cacheKey, CACHE_CONFIG.SYMBOLS_LIST);
    if (cached) return cached;

    const endpoint = assetType
      ? API_ENDPOINTS.SYMBOLS[assetType.toUpperCase() as keyof typeof API_ENDPOINTS.SYMBOLS]
      : API_ENDPOINTS.SYMBOLS.ALL;

    const url = buildApiUrl(endpoint);
    const response = await this.fetchWithRetry(url);
    const data = await response.json();

    const symbols = data.symbols || data.data || [];
    this.setCache(cacheKey, symbols);
    return symbols;
  }

  /**
   * Search symbols
   */
  async searchSymbols(query: string): Promise<Symbol[]> {
    const allSymbols = await this.getSymbols();
    const lowerQuery = query.toLowerCase();

    return allSymbols.filter(
      (s) =>
        s.symbol.toLowerCase().includes(lowerQuery) ||
        s.name.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * Get live price
   */
  async getLivePrice(symbol: string): Promise<{
    price: number;
    change: number;
    changePct: number;
  }> {
    const url = buildApiUrl(`${API_ENDPOINTS.LIVE_PRICES}?symbol=${symbol}`);
    const response = await this.fetchWithRetry(url);
    const data = await response.json();

    return {
      price: data.price || 0,
      change: data.change || 0,
      changePct: data.change_pct || 0,
    };
  }

  /**
   * Map timeframe string to API interval string
   */
  private timeframeToInterval(timeframe: string): IntervalString {
    const map: Record<string, IntervalString> = {
      weekly: '1week',
      daily: '1day',
      hourly: '1h',
      '5min': '5min',
    };
    return map[timeframe] || '1day';
  }

  /**
   * Normalize API response data for chart consumption.
   * Maps field names (datetime→time, rsi_14→rsi, atr_14→atr, bollinger_*→bb_*),
   * ensures time is a Unix timestamp number, deduplicates by time, and sorts ascending.
   */
  private normalizeForCharts(
    rawData: Record<string, unknown>[]
  ): Record<string, unknown>[] {
    const mapped = rawData.map((item) => {
      const normalized: Record<string, unknown> = { ...item };

      // Resolve `time` from multiple sources: timestamp > time > datetime
      if (normalized.time == null && typeof normalized.timestamp === 'number' && normalized.timestamp > 0) {
        normalized.time = normalized.timestamp;
      }
      if (normalized.time == null && normalized.datetime) {
        const dt = normalized.datetime as string;
        const parsed = Date.parse(dt);
        if (!isNaN(parsed)) {
          normalized.time = Math.floor(parsed / 1000);
        }
      }
      if (typeof normalized.time === 'string') {
        const parsed = Date.parse(normalized.time as string);
        if (!isNaN(parsed)) {
          normalized.time = Math.floor(parsed / 1000);
        }
      }

      // Map indicator field names (various API naming conventions)
      if (normalized.rsi == null && normalized.rsi_14 != null) {
        normalized.rsi = normalized.rsi_14;
      }
      if (normalized.atr == null && normalized.atr_14 != null) {
        normalized.atr = normalized.atr_14;
      }
      if (normalized.macd_hist == null && normalized.macd_histogram != null) {
        normalized.macd_hist = normalized.macd_histogram;
      }
      // Bollinger Bands: some endpoints return bollinger_upper/middle/lower
      if (normalized.bb_upper == null && normalized.bollinger_upper != null) {
        normalized.bb_upper = normalized.bollinger_upper;
      }
      if (normalized.bb_middle == null && normalized.bollinger_middle != null) {
        normalized.bb_middle = normalized.bollinger_middle;
      }
      if (normalized.bb_lower == null && normalized.bollinger_lower != null) {
        normalized.bb_lower = normalized.bollinger_lower;
      }

      return normalized;
    });

    // Sort ascending by time, then deduplicate (keep last entry per timestamp)
    mapped.sort((a, b) => (Number(a.time) || 0) - (Number(b.time) || 0));
    const deduped = new Map<number, Record<string, unknown>>();
    for (const item of mapped) {
      const t = Number(item.time) || 0;
      if (t > 0) {
        deduped.set(t, item);
      }
    }
    return Array.from(deduped.values());
  }

  /**
   * Universal data fetch for charts - supports all asset types and timeframes.
   * Returns an array of candle objects ready for lightweight-charts.
   * Tries asset-specific endpoints first (return indicators + unix timestamp),
   * then falls back to /api/twelvedata/.../history (OHLCV only).
   */
  async getData(
    symbol: string,
    assetType: string = 'stocks',
    timeframe: string = 'daily',
    limit: number = 100
  ): Promise<Record<string, unknown>[]> {
    // Monthly is rolled up from daily data client-side
    const isMonthly = timeframe === 'monthly';
    const fetchTimeframe = isMonthly ? 'daily' : timeframe;
    const fetchLimit = isMonthly ? 500 : limit;

    const cacheKey = `data_${symbol}_${assetType}_${timeframe}_${limit}`;
    const cached = this.getFromCache<Record<string, unknown>[]>(cacheKey, CACHE_CONFIG.MARKET_DATA);
    if (cached) return cached;

    let chartData: Record<string, unknown>[] = [];

    // Primary: asset-specific endpoints (return full indicators + unix timestamp)
    try {
      const endpoint = this.getAssetHistoryEndpoint(assetType, fetchTimeframe);
      const params = buildQueryString({ symbol, pair: symbol, limit: fetchLimit });
      const url = buildApiUrl(`${endpoint}?${params}`);
      const response = await this.fetchWithRetry(url);
      const result = await response.json();
      const raw = result.data || result.results || [];
      if (Array.isArray(raw) && raw.length > 0) {
        chartData = this.normalizeForCharts(raw);
      }
    } catch (err) {
      console.warn(`getData: asset-specific history failed for ${symbol}`, err);
    }

    // Fallback: /api/twelvedata/{assetType}/{fetchTimeframe}/history (OHLCV only, no indicators)
    if (chartData.length <= 1) {
      try {
        const params = buildQueryString({ symbol, limit: fetchLimit });
        const url = buildApiUrl(`/api/twelvedata/${assetType}/${fetchTimeframe}/history?${params}`);
        const response = await this.fetchWithRetry(url);
        const result = await response.json();
        const raw = result.data || result.results || [];
        if (Array.isArray(raw) && raw.length > 0) {
          chartData = this.normalizeForCharts(raw);
        }
      } catch (err) {
        console.warn(`getData: /api/twelvedata history also failed for ${symbol}`, err);
      }
    }

    // Roll up daily data to monthly if requested
    if (isMonthly && chartData.length > 0) {
      chartData = this.rollUpToMonthly(chartData);
    }

    this.setCache(cacheKey, chartData);
    return chartData;
  }

  /**
   * Roll up daily candles into monthly OHLCV candles.
   * Groups by year-month. Uses first open, max high, min low, last close, sum volume.
   */
  private rollUpToMonthly(dailyData: Record<string, unknown>[]): Record<string, unknown>[] {
    const monthMap = new Map<string, Record<string, unknown>[]>();

    for (const candle of dailyData) {
      const t = Number(candle.time) || 0;
      if (t <= 0) continue;
      const date = new Date(t * 1000);
      const key = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
      if (!monthMap.has(key)) monthMap.set(key, []);
      monthMap.get(key)!.push(candle);
    }

    const monthly: Record<string, unknown>[] = [];
    for (const [key, candles] of monthMap) {
      candles.sort((a, b) => (Number(a.time) || 0) - (Number(b.time) || 0));
      const first = candles[0];
      const last = candles[candles.length - 1];
      const [year, month] = key.split('-').map(Number);
      const monthTimestamp = Math.floor(new Date(Date.UTC(year, month - 1, 1)).getTime() / 1000);

      const lows = candles.map(c => Number(c.low) || Infinity).filter(v => v !== Infinity && v > 0);

      monthly.push({
        ...last,
        time: monthTimestamp,
        open: Number(first.open) || 0,
        high: Math.max(...candles.map(c => Number(c.high) || 0)),
        low: lows.length > 0 ? Math.min(...lows) : 0,
        close: Number(last.close) || 0,
        volume: candles.reduce((sum, c) => sum + (Number(c.volume) || 0), 0),
      });
    }

    return monthly.sort((a, b) => (Number(a.time) || 0) - (Number(b.time) || 0));
  }

  /**
   * Get the asset-specific history endpoint path.
   * These endpoints return full indicator data (RSI, MACD, BB, SMA, etc.)
   * plus a unix timestamp field.
   */
  private getAssetHistoryEndpoint(assetType: string, timeframe: string): string {
    // Normalize timeframe variants
    const tf = timeframe === 'fiveMin' ? '5min' : timeframe;

    const routes: Record<string, Record<string, string>> = {
      stocks: {
        daily: '/api/stocks/history',
        weekly: '/api/stocks/history',
        hourly: '/api/stocks/15min/history',
        '5min': '/api/stocks/5min/history',
      },
      crypto: {
        daily: '/api/crypto/daily/history',
        weekly: '/api/crypto/daily/history',
        hourly: '/api/crypto/daily/history',
        '5min': '/api/crypto/5min/history',
      },
      forex: {
        daily: '/api/forex/history',
        weekly: '/api/forex/history',
        hourly: '/api/forex/hourly/history',
        '5min': '/api/forex/5min/history',
      },
      etfs: {
        daily: '/api/etfs/history',
        weekly: '/api/etfs/history',
        hourly: '/api/etfs/hourly/history',
        '5min': '/api/etfs/5min/history',
      },
      indices: {
        daily: '/api/indices/history',
        weekly: '/api/indices/history',
        hourly: '/api/indices/hourly/history',
        '5min': '/api/indices/5min/history',
      },
      commodities: {
        daily: '/api/commodities/history',
        weekly: '/api/commodities/history',
        hourly: '/api/commodities/hourly/history',
        '5min': '/api/commodities/5min/history',
      },
      interest_rates: {
        daily: '/api/interest-rates/history',
        weekly: '/api/interest-rates/history',
      },
      bonds: {
        daily: '/api/bonds/history',
        weekly: '/api/bonds/history',
        hourly: '/api/bonds/history',
        '5min': '/api/bonds/history',
      },
      mutual_funds: {
        daily: '/api/mutual-funds/history',
        weekly: '/api/mutual-funds/history',
        hourly: '/api/mutual-funds/history',
        '5min': '/api/mutual-funds/history',
      },
      options: {
        daily: '/api/options/history',
        weekly: '/api/options/history',
        hourly: '/api/options/history',
        '5min': '/api/options/history',
      },
    };
    return routes[assetType]?.[tf] || `/api/${assetType}/history`;
  }

  /**
   * Fetch crypto chart data for a symbol
   */
  async getCryptoData(
    symbol: string,
    timeframe: string = 'daily',
    limit: number = 200
  ): Promise<Record<string, unknown>[]> {
    return this.getData(symbol, 'crypto', timeframe, limit);
  }

  /**
   * Fetch stock chart data for a symbol
   */
  async getStockData(
    symbol: string,
    timeframe: string = 'daily',
    limit: number = 200
  ): Promise<Record<string, unknown>[]> {
    return this.getData(symbol, 'stocks', timeframe, limit);
  }

  /**
   * Fetch all crypto pairs with latest data
   */
  async getAllCryptoPairs(): Promise<Record<string, unknown>[]> {
    const cacheKey = 'all_crypto_pairs';
    const cached = this.getFromCache<Record<string, unknown>[]>(cacheKey, CACHE_CONFIG.MARKET_DATA);
    if (cached) return cached;

    const url = buildApiUrl('/api/twelvedata/crypto/daily?limit=100');
    const response = await this.fetchWithRetry(url);
    const result = await response.json();

    const data = result.data || result.results || [];
    this.setCache(cacheKey, data);
    return data;
  }

  /**
   * Fetch all stock symbols with latest data
   */
  async getAllStockSymbols(): Promise<Record<string, unknown>[]> {
    const cacheKey = 'all_stock_symbols';
    const cached = this.getFromCache<Record<string, unknown>[]>(cacheKey, CACHE_CONFIG.MARKET_DATA);
    if (cached) return cached;

    const url = buildApiUrl('/api/twelvedata/stocks/daily?limit=100');
    const response = await this.fetchWithRetry(url);
    const result = await response.json();

    const data = result.data || result.results || [];
    this.setCache(cacheKey, data);
    return data;
  }

  /**
   * Fetch market summary (top gainers, losers, volume)
   */
  async getMarketSummary(
    market: string = 'crypto'
  ): Promise<{
    top_gainers: Record<string, unknown>[];
    top_losers: Record<string, unknown>[];
    highest_volume: Record<string, unknown>[];
  }> {
    const cacheKey = `market_summary_${market}`;
    const cached = this.getFromCache<{
      top_gainers: Record<string, unknown>[];
      top_losers: Record<string, unknown>[];
      highest_volume: Record<string, unknown>[];
    }>(cacheKey, CACHE_CONFIG.MARKET_DATA);
    if (cached) return cached;

    const assetType = market === 'stock' ? 'stocks' : market;
    const url = buildApiUrl(`/api/twelvedata/${assetType}/daily?limit=100`);
    const response = await this.fetchWithRetry(url);
    const result = await response.json();

    const allData: Record<string, unknown>[] = result.data || result.results || [];

    // Derive summary from the data
    const sorted = [...allData].sort(
      (a, b) => (Number(b.roc) || 0) - (Number(a.roc) || 0)
    );
    const summary = {
      top_gainers: sorted.filter((d) => (Number(d.roc) || 0) > 0).slice(0, 10),
      top_losers: sorted
        .filter((d) => (Number(d.roc) || 0) < 0)
        .reverse()
        .slice(0, 10),
      highest_volume: [...allData]
        .sort((a, b) => (Number(b.volume) || 0) - (Number(a.volume) || 0))
        .slice(0, 10),
    };

    this.setCache(cacheKey, summary);
    return summary;
  }

  // ============================================================================
  // § 2. DATA ENHANCEMENT
  // ============================================================================

  /**
   * Enhance candle data with calculated fields
   */
  private enhanceWithCalculations(
    candles: CandleWithIndicators[]
  ): CandleWithIndicators[] {
    return candles.map((candle, index) => {
      // Calculate growth score
      const growthScore = calculateGrowthScore({
        rsi_14: candle.rsi_14,
        macd_histogram: candle.macd_histogram,
        adx: candle.adx,
        close: candle.close,
        sma_200: candle.sma_200,
      });

      // Classify trend regime
      const trendRegime = classifyTrendRegime(
        candle.close,
        candle.sma_50 || candle.close,
        candle.sma_200 || candle.close,
        candle.adx || 0
      );

      // Detect EMA cycle
      const inRiseCycle =
        candle.ema_12 !== undefined &&
        candle.ema_26 !== undefined &&
        candle.ema_12 > candle.ema_26;

      // Detect cycle starts
      let riseCycleStart = false;
      let fallCycleStart = false;

      if (index > 0) {
        const prev = candles[index - 1];
        if (
          candle.ema_12 !== undefined &&
          candle.ema_26 !== undefined &&
          prev.ema_12 !== undefined &&
          prev.ema_26 !== undefined
        ) {
          riseCycleStart =
            candle.ema_12 > candle.ema_26 && prev.ema_12 <= prev.ema_26;
          fallCycleStart =
            candle.ema_12 < candle.ema_26 && prev.ema_12 >= prev.ema_26;
        }
      }

      return {
        ...candle,
        growth_score: growthScore,
        trend_regime: trendRegime,
        in_rise_cycle: inRiseCycle,
        rise_cycle_start: riseCycleStart,
        fall_cycle_start: fallCycleStart,
      };
    });
  }

  // ============================================================================
  // § 3. HELPER METHODS
  // ============================================================================

  /**
   * Fetch with retry logic
   */
  private async fetchWithRetry(
    url: string,
    options: RequestInit = {},
    retries: number = DEFAULT_REQUEST_OPTIONS.retries
  ): Promise<Response> {
    // Only include Content-Type for requests with a body (POST/PUT/PATCH).
    // Adding it to GET requests triggers a CORS preflight that the server may reject.
    const isBodyRequest = options.method && !['GET', 'HEAD'].includes(options.method.toUpperCase());
    const headers: Record<string, string> = {
      ...(isBodyRequest ? DEFAULT_REQUEST_OPTIONS.headers : {}),
      ...(options.headers as Record<string, string>),
    };

    const fetchOptions: RequestInit = {
      ...options,
      headers,
    };

    for (let i = 0; i < retries; i++) {
      try {
        const response = await fetch(url, fetchOptions);
        if (response.ok) return response;

        if (response.status >= 500 && i < retries - 1) {
          await this.delay(DEFAULT_REQUEST_OPTIONS.retryDelay * (i + 1));
          continue;
        }

        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      } catch (error) {
        if (i === retries - 1) throw error;
        await this.delay(DEFAULT_REQUEST_OPTIONS.retryDelay * (i + 1));
      }
    }

    throw new Error('Max retries exceeded');
  }

  /**
   * Get from cache if valid
   */
  private getFromCache<T>(key: string, maxAge: number): T | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < maxAge) {
      return cached.data as T;
    }
    return null;
  }

  /**
   * Set cache
   */
  private setCache(key: string, data: unknown): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Delay helper
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

// ============================================================================
// § 4. SINGLETON EXPORT
// ============================================================================

export const marketDataService = new MarketDataService();
export default marketDataService;
